import UploadIcon from "@mui/icons-material/Upload";
import Button, { type ButtonProps } from "@mui/material/Button";
import { buttonBaseClasses } from "@mui/material/ButtonBase";
import useId from "@mui/utils/useId";
import {
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  type KeyboardEvent,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { VisuallyHiddenInput } from "../VisuallyHiddenInput";

export function DropZone({
  error,
  buttonProps,
  disabled,
  inputProps,
  name,
  handleFile,
  label,
}: {
  error?: boolean;
  buttonProps?: ButtonProps;
  disabled?: boolean;
  name: string;
  inputProps?: DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
  handleFile: (file: File) => void | Promise<void>;
  label?: string;
}) {
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:DropZone" });
  const dropText = t("dropText", "Drop file here");
  const defaultLabel = t("defaultLabel", "Click or drag file to upload");
  const actualLabel = label || defaultLabel;
  const [isDragging, setIsDragging] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const inputId = useId();

  const onDrop = async (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const file = e.dataTransfer?.items?.[0]?.getAsFile();
    if (!file) {
      return;
    }
    setIsChanging(true);
    try {
      await handleFile(file);
    } finally {
      setIsChanging(false);
    }
  };
  return (
    <Button
      component="label"
      sx={{
        minHeight: "100px",
        width: "100%",
        border: (theme) => {
          let color = isDragging
            ? theme.palette.primary.main
            : theme.palette.primary.lighter;
          if (error) {
            color = theme.palette.error.light;
          }
          return `1px dashed ${color}`;
        },
        [`&.${buttonBaseClasses.disabled}`]: {
          border: (theme) => `2px dashed ${theme.palette.divider}`,
        },
        ...buttonProps?.sx,
      }}
      onDragOver={(e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      onDrop={(e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsDragging(false);
        onDrop(e);
      }}
      onKeyUp={(e: KeyboardEvent) => {
        if (
          e !== null &&
          e.key === "Enter" &&
          e.currentTarget !== null &&
          "click" in e.currentTarget &&
          typeof e.currentTarget.click === "function"
        ) {
          e.currentTarget.click();
        }
      }}
      disabled={disabled}
      variant="outlined"
      color={error ? "error" : "primary"}
      loadingPosition="start"
      loading={isChanging}
      startIcon={<UploadIcon />}
      {...buttonProps}
    >
      {isDragging ? dropText : actualLabel}
      <VisuallyHiddenInput
        type="file"
        name={name}
        tabIndex={-1}
        id={inputId}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) {
            return;
          }
          try {
            setIsChanging(true);
            await handleFile(file);
          } finally {
            e.target.value = "";
            setIsChanging(false);
          }
        }}
        {...inputProps}
      />
    </Button>
  );
}
